import { useEffect, useState } from "react";
import Chart, { ChartItem } from "chart.js/auto";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Paper,
  Chip,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Project } from "../../features/projects/Project";
import { Milestone } from "../../features/projects/Milestone";

const METRIC_TOOLTIPS: Record<string, string> = {
  "Total estimate":
    "The milestone budget (hours) from [ESTIMATED_HOURS] in the milestone description. This is the number communicated to the client.",
  "Actual burnup":
    "Cumulative actual hours logged in Clockify for this milestone. Shows real work done over time.",
  "Estimate burnup":
    "Projected hours completed based on task completion ratio: Total Estimate x (Completed Tasks / Total Tasks).",
  "Est. Total hours":
    "Predicted total hours needed to finish the milestone: Actual Burnup x (Milestone Estimate / Completed Task Estimates). Decreasing means improving efficiency.",
  "Total Task Estimate":
    "Sum of individual task time estimates (hours) for all tasks created up to each date.",
  "Total Of Est. Completed":
    "Sum of time estimates (hours) for tasks that have been closed/completed.",
  "Total Tasks":
    "Count of tasks created in this milestone up to each date.",
  "Gain From Estimate":
    "Estimate Burnup minus Actual Burnup. Positive (green) means you're ahead of the estimate; negative (red) means behind.",
  "Actual / Total Task Est.":
    "Ratio of actual hours worked to total task estimates. Below 1.0 = under budget per task, above 1.0 = over budget.",
  "Actual / Total Estimate":
    "Ratio of actual hours worked to the milestone estimate. Below 1.0 = under the client budget, above 1.0 = over budget.",
  "Actual / Estimate Burnup":
    "Ratio of actual hours to estimate burnup. Below 1.0 = completing tasks with less effort than estimated.",
};

interface ProjectProgressReportProps {
  projects: Project[];
  selectedProject: Project;
  selectedMilestone: Milestone;
  handleProjectChange?: (event: any) => void;
  handleMilestoneChange?: (event: any) => void;
}

const ProjectProgressReport = ({
  selectedProject,
  selectedMilestone,
}: ProjectProgressReportProps) => {
  const [chart, setChart] = useState<Chart | null>(null);
    
  useEffect(() => {
    if (!selectedMilestone) {
      return () => {};
    }

    const chartItem = document.getElementById("chart-metrics") as ChartItem;

    if (chart != null) {
      chart.data.labels =
        selectedMilestone?.totalEstimate?.map((item) =>
          new Date(item.date).toLocaleDateString()
        ) || [];
      chart.data.datasets = [
        {
          label: "Total Estimate",
          tension: 0.4,
          data:
            selectedMilestone?.totalEstimate?.map((item) => item.value) || [],
          borderColor: "rgba(75, 192, 192, 1)",
          fill: false,
        },
        {
          label: "Actual Burnup",
          tension: 0.4,
          data:
            selectedMilestone?.actualBurnUp?.map((item) => item.value) || [],
          borderColor: "rgba(255, 99, 132, 1)",
          fill: false,
        },
        {
          label: "Estimate Burnup",
          tension: 0.4,
          data:
            selectedMilestone?.estimateBurnUp?.map((item) => item.value) || [],
          borderColor: "rgba(255, 159, 64, 1)",
          fill: false,
        },
        {
          label: "Estimated Total Hours Needed",
          tension: 0.4,
          data:
            selectedMilestone?.estimatedTotalHoursNeeded?.map(
              (item) => item.value
            ) || [],
          borderColor: "rgba(153, 102, 255, 1)",
          fill: false,
        },
        {
          label: "Total Task Estimate",
          tension: 0.4,
          data:
            selectedMilestone?.totalTaskEstimates?.map((item) => item.value) ||
            [],
          borderColor: "rgba(54, 162, 235, 1)",
          fill: false,
        },
        {
          label: "Total Of Estimated Completed",
          tension: 0.4,
          data:
            selectedMilestone?.totalOfEstimatedCompleted?.map(
              (item) => item.value
            ) || [],
          borderColor: "rgba(255, 105, 180, 1)",
          fill: false,
        },
        {
          label: "Total Tasks",
          tension: 0.4,
          data: selectedMilestone?.totalTasks?.map((item) => item.value) || [],
          borderColor: "rgba(201, 203, 207, 1)",
          fill: false,
        },
      ];
      chart.update();
    } else {
      const newChart = new Chart(chartItem, {
        type: "line",
        data: {
          labels: selectedMilestone.totalEstimate.map((item) =>
            new Date(item.date).toLocaleDateString()
          ),
          datasets: [
            {
              label: "Total Estimate",
              tension: 0.4,
              data: selectedMilestone.totalEstimate.map((item) => item.value),
              borderColor: "rgba(75, 192, 192, 1)",
              fill: false,
            },
            {
              label: "Actual Burnup",
              tension: 0.4,
              data: selectedMilestone.actualBurnUp.map((item) => item.value),
              borderColor: "rgba(255, 99, 132, 1)",
              fill: false,
            },
            {
              label: "Estimate Burnup",
              tension: 0.4,
              data: selectedMilestone.estimateBurnUp.map((item) => item.value),
              borderColor: "rgba(255, 159, 64, 1)",
              fill: false,
            },
            {
              label: "Estimated Total Hours Needed",
              tension: 0.4,
              data: selectedMilestone.estimatedTotalHoursNeeded.map(
                (item) => item.value
              ),
              borderColor: "rgba(153, 102, 255, 1)",
              fill: false,
            },
            {
              label: "Total Task Estimate",
              tension: 0.4,
              data: selectedMilestone.totalTaskEstimates.map(
                (item) => item.value
              ),
              borderColor: "rgba(54, 162, 235, 1)",
              fill: false,
            },
            {
              label: "Total Of Estimated Completed",
              tension: 0.4,
              data: selectedMilestone.totalOfEstimatedCompleted.map(
                (item) => item.value
              ),
              borderColor: "rgba(255, 105, 180, 1)",
              fill: false,
            },
            {
              label: "Total Tasks",
              tension: 0.4,
              data: selectedMilestone.totalTasks.map((item) => item.value),
              borderColor: "rgba(201, 203, 207, 1)",
              fill: false,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              position: 'bottom',
              labels: {
                boxWidth: 12,
                padding: 15,
                font: {
                  size: 11,
                },
              },
            },
            tooltip: {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              padding: 12,
              titleFont: {
                size: 13,
              },
              bodyFont: {
                size: 12,
              },
              cornerRadius: 4,
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              grid: {
                color: 'rgba(0, 0, 0, 0.05)',
              },
            },
            x: {
              grid: {
                display: false,
              },
            },
          },
        },
      });
      setChart(newChart);
    }

    return () => {};
  }, [selectedMilestone, chart]);

  useEffect(() => {
    return () => {
      if (chart) {
        chart.destroy();
      }
    };
  }, [chart]);

  const ratio =
    (selectedMilestone?.actualBurnUp?.[
      (selectedMilestone?.actualBurnUp?.length || 1) - 1
    ]?.value || 0) /
    (selectedMilestone?.totalTaskEstimates?.[
      (selectedMilestone?.totalTaskEstimates?.length || 1) - 1
    ]?.value || 1);

  const actualToTotalEstimateRatio =
    (selectedMilestone?.actualBurnUp?.[
      (selectedMilestone?.actualBurnUp?.length || 1) - 1
    ]?.value || 0) /
    (selectedMilestone?.totalEstimate?.[
      (selectedMilestone?.totalEstimate?.length || 1) - 1
    ]?.value || 1);

  const actualToEstimateBurnupRatio =
    (selectedMilestone?.actualBurnUp?.[
      (selectedMilestone?.actualBurnUp?.length || 1) - 1
    ]?.value || 0) /
    (selectedMilestone?.estimateBurnUp?.[
      (selectedMilestone?.estimateBurnUp?.length || 1) - 1
    ]?.value || 1);

  let color =
    (ratio <= 1 && "rgba(75, 192, 192, 1)") ||
    (ratio > 1 && ratio <= 1.2 && "rgba(255, 159, 64, 1)") ||
    "rgba(255, 99, 132, 1)";

  let actualToTotalEstimateColor =
    (actualToTotalEstimateRatio <= 1 && "rgba(75, 192, 192, 1)") ||
    (actualToTotalEstimateRatio > 1 &&
      actualToTotalEstimateRatio <= 1.2 &&
      "rgba(255, 159, 64, 1)") ||
    "rgba(255, 99, 132, 1)";

  let actualToEstimateBurnupColor =
    (actualToEstimateBurnupRatio <= 1 && "rgba(75, 192, 192, 1)") ||
    (actualToEstimateBurnupRatio > 1 &&
      actualToEstimateBurnupRatio <= 1.2 &&
      "rgba(255, 159, 64, 1)") ||
    "rgba(255, 99, 132, 1)";

  const lastKnownDate =
    selectedMilestone?.totalEstimate?.[
      (selectedMilestone?.totalEstimate?.length || 1) - 1
    ]?.date;

  const formattedLastKnownDate = lastKnownDate
    ? new Date(lastKnownDate).toLocaleString()
    : "No data available";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        mt: 3,
      }}
    >
      {/* Chart Section */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          borderRadius: 2,
          background: "linear-gradient(to bottom, #ffffff, #f8f9fa)",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: { xs: "350px", sm: "400px", md: "450px" },
            position: "relative",
          }}
        >
          <Box
            component="canvas"
            id="chart-metrics"
            sx={{
              height: {
                xs: "350px !important",
                sm: "400px !important",
                md: "450px !important",
              },
            }}
          />
        </Box>
      </Paper>

      {/* Metrics Cards Grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", lg: "1fr 1fr 1fr" },
          gap: 2,
        }}
      >
        {/* Latest Values Card */}
        <Card elevation={2} sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
            <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 600 }}>
              Latest Values
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1,
                  borderLeft: "3px solid rgba(75, 192, 192, 1)",
                  pl: 1.5,
                  backgroundColor: "rgba(75, 192, 192, 0.05)",
                  borderRadius: "0 4px 4px 0",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Total estimate
                  </Typography>
                  <Tooltip title={METRIC_TOOLTIPS["Total estimate"]} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                  </Tooltip>
                </Box>
                <Typography variant="body1" fontWeight={600}>
                  {selectedMilestone
                    ? (
                        selectedMilestone.totalEstimate[
                          (selectedMilestone.totalEstimate.length || 1) - 1
                        ]?.value || 0
                      ).toFixed(2)
                    : "N/A"}
                  h
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1,
                  borderLeft: "3px solid rgba(255, 99, 132, 1)",
                  pl: 1.5,
                  backgroundColor: "rgba(255, 99, 132, 0.05)",
                  borderRadius: "0 4px 4px 0",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Actual burnup
                  </Typography>
                  <Tooltip title={METRIC_TOOLTIPS["Actual burnup"]} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                  </Tooltip>
                </Box>
                <Typography variant="body1" fontWeight={600}>
                  {(
                    selectedMilestone?.actualBurnUp?.[
                      (selectedMilestone?.actualBurnUp?.length || 1) - 1
                    ]?.value || 0
                  ).toFixed(2)}
                  h
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1,
                  borderLeft: "3px solid rgba(255, 159, 64, 1)",
                  pl: 1.5,
                  backgroundColor: "rgba(255, 159, 64, 0.05)",
                  borderRadius: "0 4px 4px 0",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Estimate burnup
                  </Typography>
                  <Tooltip title={METRIC_TOOLTIPS["Estimate burnup"]} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                  </Tooltip>
                </Box>
                <Typography variant="body1" fontWeight={600}>
                  {(
                    selectedMilestone?.estimateBurnUp?.[
                      (selectedMilestone?.estimateBurnUp?.length || 1) - 1
                    ]?.value || 0
                  ).toFixed(2)}
                  h
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1,
                  borderLeft: "3px solid rgba(153, 102, 255, 1)",
                  pl: 1.5,
                  backgroundColor: "rgba(153, 102, 255, 0.05)",
                  borderRadius: "0 4px 4px 0",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Est. Total hours
                  </Typography>
                  <Tooltip title={METRIC_TOOLTIPS["Est. Total hours"]} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                  </Tooltip>
                </Box>
                <Typography variant="body1" fontWeight={600}>
                  {(
                    selectedMilestone?.estimatedTotalHoursNeeded?.[
                      (selectedMilestone?.estimatedTotalHoursNeeded?.length || 1) -
                        1
                    ]?.value || 0
                  ).toFixed(2)}
                  h
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1,
                  borderLeft: "3px solid rgba(54, 162, 235, 1)",
                  pl: 1.5,
                  backgroundColor: "rgba(54, 162, 235, 0.05)",
                  borderRadius: "0 4px 4px 0",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Total Task Estimate
                  </Typography>
                  <Tooltip title={METRIC_TOOLTIPS["Total Task Estimate"]} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                  </Tooltip>
                </Box>
                <Typography variant="body1" fontWeight={600}>
                  {(
                    selectedMilestone?.totalTaskEstimates?.[
                      (selectedMilestone?.totalTaskEstimates?.length || 1) - 1
                    ]?.value || 0
                  ).toFixed(2)}
                  h
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1,
                  borderLeft: "3px solid rgba(255, 105, 180, 1)",
                  pl: 1.5,
                  backgroundColor: "rgba(255, 105, 180, 0.05)",
                  borderRadius: "0 4px 4px 0",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Total Of Est. Completed
                  </Typography>
                  <Tooltip title={METRIC_TOOLTIPS["Total Of Est. Completed"]} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                  </Tooltip>
                </Box>
                <Typography variant="body1" fontWeight={600}>
                  {(
                    selectedMilestone?.totalOfEstimatedCompleted?.[
                      (selectedMilestone?.totalOfEstimatedCompleted?.length || 1) -
                        1
                    ]?.value || 0
                  ).toFixed(2)}
                  h
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1,
                  borderLeft: "3px solid rgba(201, 203, 207, 1)",
                  pl: 1.5,
                  backgroundColor: "rgba(201, 203, 207, 0.05)",
                  borderRadius: "0 4px 4px 0",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Total Tasks
                  </Typography>
                  <Tooltip title={METRIC_TOOLTIPS["Total Tasks"]} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                  </Tooltip>
                </Box>
                <Typography variant="body1" fontWeight={600}>
                  {(
                    selectedMilestone?.totalTasks?.[
                      (selectedMilestone?.totalTasks?.length || 1) - 1
                    ]?.value || 0
                  ).toFixed(0)}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1,
                  borderLeft: "3px solid rgba(0, 0, 133, 1)",
                  pl: 1.5,
                  backgroundColor: "rgba(0, 0, 133, 0.05)",
                  borderRadius: "0 4px 4px 0",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Gain From Estimate
                  </Typography>
                  <Tooltip title={METRIC_TOOLTIPS["Gain From Estimate"]} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                  </Tooltip>
                </Box>
                <Chip
                  label={
                    (
                      (selectedMilestone?.estimateBurnUp?.[
                        (selectedMilestone?.estimateBurnUp?.length || 1) - 1
                      ]?.value || 0) -
                      (selectedMilestone?.actualBurnUp?.[
                        (selectedMilestone?.actualBurnUp?.length || 1) - 1
                      ]?.value || 0)
                    ).toFixed(2) === "0.00"
                      ? "0h"
                      : `${(
                          (selectedMilestone?.estimateBurnUp?.[
                            (selectedMilestone?.estimateBurnUp?.length || 1) - 1
                          ]?.value || 0) -
                          (selectedMilestone?.actualBurnUp?.[
                            (selectedMilestone?.actualBurnUp?.length || 1) - 1
                          ]?.value || 0)
                        ).toFixed(2)}h`
                  }
                  size="small"
                  sx={{
                    fontWeight: 600,
                    backgroundColor:
                      (selectedMilestone?.estimateBurnUp?.[
                        (selectedMilestone?.estimateBurnUp?.length || 1) - 1
                      ]?.value || 0) -
                        (selectedMilestone?.actualBurnUp?.[
                          (selectedMilestone?.actualBurnUp?.length || 1) - 1
                        ]?.value || 0) ===
                      0
                        ? "rgba(75, 192, 192, 0.15)"
                        : (selectedMilestone?.estimateBurnUp?.[
                            (selectedMilestone?.estimateBurnUp?.length || 1) - 1
                          ]?.value || 0) -
                            (selectedMilestone?.actualBurnUp?.[
                              (selectedMilestone?.actualBurnUp?.length || 1) - 1
                            ]?.value || 0) >
                          0
                        ? "rgba(75, 192, 192, 0.15)"
                        : "rgba(255, 99, 132, 0.15)",
                    color:
                      (selectedMilestone?.estimateBurnUp?.[
                        (selectedMilestone?.estimateBurnUp?.length || 1) - 1
                      ]?.value || 0) -
                        (selectedMilestone?.actualBurnUp?.[
                          (selectedMilestone?.actualBurnUp?.length || 1) - 1
                        ]?.value || 0) >=
                      0
                        ? "rgba(75, 192, 192, 1)"
                        : "rgba(255, 99, 132, 1)",
                  }}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Ratios Card */}
        <Card elevation={2} sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
            <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 600 }}>
              Ratios
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 1.5,
                  backgroundColor: "rgba(0, 0, 0, 0.02)",
                  borderRadius: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Actual / Total Task Est.
                  </Typography>
                  <Tooltip title={METRIC_TOOLTIPS["Actual / Total Task Est."]} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                  </Tooltip>
                </Box>
                <Chip
                  label={ratio.toFixed(2)}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    backgroundColor:
                      ratio <= 1
                        ? "rgba(75, 192, 192, 0.15)"
                        : ratio <= 1.2
                        ? "rgba(255, 159, 64, 0.15)"
                        : "rgba(255, 99, 132, 0.15)",
                    color: color,
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 1.5,
                  backgroundColor: "rgba(0, 0, 0, 0.02)",
                  borderRadius: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Actual / Total Estimate
                  </Typography>
                  <Tooltip title={METRIC_TOOLTIPS["Actual / Total Estimate"]} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                  </Tooltip>
                </Box>
                <Chip
                  label={actualToTotalEstimateRatio.toFixed(2)}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    backgroundColor:
                      actualToTotalEstimateRatio <= 1
                        ? "rgba(75, 192, 192, 0.15)"
                        : actualToTotalEstimateRatio <= 1.2
                        ? "rgba(255, 159, 64, 0.15)"
                        : "rgba(255, 99, 132, 0.15)",
                    color: actualToTotalEstimateColor,
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 1.5,
                  backgroundColor: "rgba(0, 0, 0, 0.02)",
                  borderRadius: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Actual / Estimate Burnup
                  </Typography>
                  <Tooltip title={METRIC_TOOLTIPS["Actual / Estimate Burnup"]} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.disabled", cursor: "help" }} />
                  </Tooltip>
                </Box>
                <Chip
                  label={actualToEstimateBurnupRatio.toFixed(2)}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    backgroundColor:
                      actualToEstimateBurnupRatio <= 1
                        ? "rgba(75, 192, 192, 0.15)"
                        : actualToEstimateBurnupRatio <= 1.2
                        ? "rgba(255, 159, 64, 0.15)"
                        : "rgba(255, 99, 132, 0.15)",
                    color: actualToEstimateBurnupColor,
                  }}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Last Known Date Card */}
        <Card elevation={2} sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
            <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 600 }}>
              Last Known Date
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Date and time stamp:
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {formattedLastKnownDate}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default ProjectProgressReport;
